/* eslint @typescript-eslint/naming-convention: 0 */
import { normalizeSearchKeyword } from '@/wabunka-src/common/utils/googleAnalytics/common/searchKeyword';

/**
 * ③「売れるクエリ」専用の購入キーワード引き継ぎ。
 *
 * 検索結果クリック時の入力キーワードを localStorage に保存し、ルックバック期間内の `purchase` に
 * `search_keyword` として付与する。これにより GA4 探索レポートで「keyword 別の売上・購入数」を出せる。
 *
 * ※ それ以外の売上帰属（① 検索アシスト売上 / ④ 売れる体験 / ⑥ トレンド / ①' 検索 vs 非検索）は、
 *   GA4 のシーケンスセグメント（「`search_result_click` → ルックバック内に `purchase`」のユーザースコープ）で
 *   query-time に算出する方針のため、purchase へ `search_assisted` 等は付与しない。
 *   ③ だけは「どの語がどの購入に紐づくか」を探索で結合できず、購入イベントに keyword を持たせる必要があるため例外的に残す。
 */
const STORAGE_KEY = 'wabunka_search_touch';

/**
 * ルックバック期間（日）。暫定値。検討〜予約までのリードタイム次第で運用調整するため定数化している。
 */
const LOOKBACK_DAYS = 7;
const LOOKBACK_MS = LOOKBACK_DAYS * 24 * 60 * 60 * 1000;

interface SearchTouch {
  /** PII ガード済みの入力キーワード（正規化後） */
  keyword: string;
  /** 保存時刻（epoch ミリ秒） */
  ts: number;
}

/** purchase に付与する ③ 用パラメータ（keyword が無い／期限切れなら付与しない） */
export interface PurchaseSearchKeywordParam {
  search_keyword?: string;
}

/**
 * 検索結果クリック時の入力キーワードを保存する（last-touch 方式：複数検索時は直近の語を購入に紐付ける）。
 * タグ直接クリック等で keyword が空のときも保存はするが、空語は ③ では集計対象にしない（build 側で除外）。
 */
export const saveSearchTouchKeyword = (keyword: string) => {
  // SSR（localStorage 不在）ではスキップ。client では happy-dom 含め localStorage が存在する。
  if (typeof localStorage === 'undefined') return;
  try {
    const value: SearchTouch = { keyword, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage が使えない環境では諦める（致命的ではない）
  }
};

const readSearchTouch = (): SearchTouch | null => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    // 破損・改ざんされた値が購入イベントへ混入しないよう型検証し、keyword は再正規化する
    const touch = JSON.parse(raw) as Partial<SearchTouch>;
    if (typeof touch?.ts !== 'number') return null;
    if (typeof touch.keyword !== 'string') return null;
    return { keyword: normalizeSearchKeyword(touch.keyword), ts: touch.ts };
  } catch {
    return null;
  }
};

/**
 * purchase へ付与する ③ 用の `search_keyword` を組み立てる。
 * ルックバック内に非空のキーワードのタッチがあれば付与、無ければ空オブジェクト（付与しない）。
 */
export const buildPurchaseSearchKeywordParam = (): PurchaseSearchKeywordParam => {
  const touch = readSearchTouch();
  if (!touch || !touch.keyword) return {};

  const elapsedMs = Date.now() - touch.ts;
  if (elapsedMs < 0 || elapsedMs > LOOKBACK_MS) return {};

  return { search_keyword: touch.keyword };
};
